import { notifyFailure } from 'app/Notifications/actions';
import isPageSelectedSelector from 'app/Page/selectors/isPageSelected';
import { visitSearch } from 'app/Router/actions';

import {
  addSearchFilter,
  clearSearchResults,
  deleteSearchFilter,
  getSearchSuggestionsFailure,
  getSearchSuggestionsRequest,
  getSearchSuggestionsSuccess,
  getSearchResultsFailure,
  getSearchResultsRequest,
  getSearchResultsSuccess,
  storeSearchResultPageNum,
  storeSearchContext
} from './actions';

/**
 * get search results
 * @param {string} context - the search context
 * @param {string} filters - the filters for search bar
 * @returns {undefined}
 */
export const getSearchResults = (context, filters) => async (dispatch, getState, { api }) => {
  try {
    dispatch(getSearchResultsRequest(context, filters));
    const results = await api.search.getSearchResults(context, filters);
    dispatch(getSearchResultsSuccess(context, results));
  } catch (error) {
    dispatch(getSearchResultsFailure(context, filters, error));
    dispatch(notifyFailure(error.message));
  }
};

/**
 * get search suggestions
 * @param {string} context - the search context
 * @param {string} inputValue - the value of the input for search bar
 * @param {object[]} filters - an array existing of simple filter objects
 * @returns {undefined}
 */
export const getSearchSuggestions = (context, inputValue, filters) => async (
  dispatch,
  getState,
  { api }
) => {
  try {
    dispatch(getSearchSuggestionsRequest(context, inputValue));
    const response = await api.search.getSearchSuggestions(context, inputValue, filters);
    dispatch(getSearchSuggestionsSuccess(context, response));
  } catch (error) {
    dispatch(getSearchSuggestionsFailure(context, inputValue, error));
    dispatch(notifyFailure(error.message));
  }
};

/**
 * update filter additions/deletions
 * @param {string} context - search context
 * @param {Function} dispatch - dispatch function
 * @param {Function} getState - current state
 * @returns {undefined}
 */
const updateFilterChange = (context, dispatch, getState) => {
  const state = getState();
  const isPageSelected = isPageSelectedSelector(state);
  const simpleFilters = state.search[context].filters;

  // don't show results when there are no filters
  if (!simpleFilters.length) {
    dispatch(clearSearchResults());
    return;
  }

  // dont update url when a page is selected
  if (!isPageSelected) {
    dispatch(visitSearch(context, simpleFilters));
  }
  dispatch(getSearchResults(context, simpleFilters));
};

/**
 * clear all filters
 * @returns {undefined}
 */
export const clearAllFilters = () => dispatch => {
  dispatch(clearSearchResults());
};

/**
 * add a search filter
 * @param {string} context - the search context
 * @param {string} k - the filter key
 * @param {string} v - the filter value
 * @returns {undefined}
 */
export const addFilter = (context, k, v) => (dispatch, getState) => {
  /* validate the value associated with the 'DateCreated' key (example value: '2019-01-01--2020-01-01')
  to ensure the start date (using above example: '2019-01-01') is less than the end date ('2020-01-01) */
  if (k === 'DateCreated' && v.slice(0, 10) > v.slice(12)) {
    dispatch(notifyFailure('your "Date Created" start date must occur before your end date'));
  } else {
    dispatch(addSearchFilter(context, k, v));
    updateFilterChange(context, dispatch, getState);
  }
};

/**
 * delete a search filter
 * @param {string} context - the search context
 * @param {string} k - the filter key
 * @param {string} v - the filter value
 * @returns {undefined}
 */
export const deleteFilter = (context, k, v) => (dispatch, getState) => {
  dispatch(deleteSearchFilter(context, k, v));
  updateFilterChange(context, dispatch, getState);
};

/**
 * store the current page number
 * @param {string} context - the search context
 * @param {string} pageNum - the page number to store
 * @param {number} pageNumStart - the beginning page number within the current page results range
 * @returns {undefined}
 */
export const storeResultPageNum = (context, pageNum, pageNumStart = 1) => dispatch => {
  dispatch(storeSearchResultPageNum(context, { pageNum, pageNumStart }));
};

/**
 * store the current search context
 * @param {string} context - the search context
 * @returns {undefined}
 */
export const storeContext = context => dispatch => {
  dispatch(storeSearchContext(context));
};
